import React from 'react'
import { Box, ButtonBase, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { type ReopenTargetStatus } from '../bulkSectionStatusActions'

/**
 * Step 2 of Bulk Reopen: what the reopened surveys should become.
 *
 * A step of its own rather than a control on the date form, because the two answers ask for
 * different dates — publishing needs a start, going live does not. Putting the fork first means
 * each date form shows only the fields that apply to it, instead of one field appearing and
 * disappearing under the admin.
 *
 * Each card commits on click, with no separate Next. A two-option fork does not need two clicks,
 * and the card body already says what the next step will ask for, which is what stops an admin
 * choosing, seeing unexpected fields, and coming back.
 *
 * The selection lives in the parent modal, so Back from here returns to it intact — including a
 * Select All that carries exclusions rather than a list of ids.
 *
 * Spec: docs/specs/active/bulk-reopen-live-or-publish/spec.md
 */

interface BulkReopenModeChoiceProps {
  selectionCount: number
  onChoose: (mode: ReopenTargetStatus) => void
  onBack: () => void
}

interface ModeOption {
  mode: ReopenTargetStatus
  title: string
  body: string
}

/**
 * Two sentences each, parallel: when it opens, and what that means for the admin.
 *
 * The second sentence is the whole reason the choice exists. Publishing is what an admin picks
 * when the survey still needs work — a survey that has not opened can still be changed, and the
 * start date in particular locks the moment a section goes live (`canEditStartDate` in
 * `CreateAndEditCourses` allows it for Draft and Published only).
 */
const OPTIONS: ModeOption[] = [
  {
    mode: 'live',
    title: 'Go Live',
    // "The moment you confirm" rather than "now": the batch is queued, and an admin who reads
    // "now" and then watches the table for a few seconds should not think it failed.
    body: 'Opens the moment you confirm. Students start responding right away.'
  },
  {
    mode: 'published',
    // "Publish" is the status the section actually takes, but the word is already spent on the
    // project-level Draft → Published action, and the row the admin sees afterwards reads
    // Upcoming. The parenthetical carries the verb so the card names both the state and the act.
    // Decided 2026-08-25.
    title: 'Publish (Schedule)',
    body: 'Opens on the date you choose. You can still make changes until then.'
  }
]

const BulkReopenModeChoice: React.FC<BulkReopenModeChoiceProps> = ({
  selectionCount,
  onChoose,
  onBack
}) => (
  <Box display='flex' flexDirection='column' gap={3}>
    <Typography fontSize='14px'>
      How should {selectionCount.toLocaleString()} course section survey{selectionCount === 1 ? '' : 's'} open?
    </Typography>

    <Box display='flex' flexDirection='column' gap={2}>
      {OPTIONS.map((option) => (
        <ButtonBase
          key={option.mode}
          onClick={() => { onChoose(option.mode) }}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            textAlign: 'left',
            width: '100%',
            padding: '16px',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '4px',
            '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
            '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' }
          }}
        >
          <Box display='flex' flexDirection='column' gap={0.5}>
            <Typography fontSize='14px' fontWeight={600}>{option.title}</Typography>
            <Typography fontSize='13px' color='text.secondary'>{option.body}</Typography>
          </Box>
          <ArrowForwardIcon fontSize='small' sx={{ color: 'text.secondary', mt: 0.25 }} />
        </ButtonBase>
      ))}
    </Box>

    {/* No confirm button: the cards are the commit, so a Next here would be a second click for a
        choice already made. Back sits where Cancel normally does. */}
    <DefaultModalActions
      confirmButtonProps={{ hideButton: true }}
      destructiveButtonProps={{ text: 'Back', onClick: onBack }}
    />
  </Box>
)

export default BulkReopenModeChoice
